/* eslint-disable @typescript-eslint/no-explicit-any */
import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { Database, withLogs, KEY_SWING_POSITIONS, CAMERA_ANGLES, AVAILABLE_PLAYBACK_SPEEDS } from '@core';

export type KeySwingPosition = (typeof KEY_SWING_POSITIONS)[number];
export type CameraAngle = (typeof CAMERA_ANGLES)[number];
export type AvailablePlaybackSpeed = (typeof AVAILABLE_PLAYBACK_SPEEDS)[number];
export type CorridorPositions = { [Key in KeySwingPosition]?: boolean };

export const defaultFloorSettings: FloorSettings = {
    playbackSpeed: 0.25,
    cameraAngle: undefined,
    instantPlayback: true,
};

export interface FloorSettings {
    instantPlayback: boolean;
    playbackSpeed: AvailablePlaybackSpeed;
    cameraAngle?: CameraAngle | null;
}

export const upsertFloorSettings = withLogs(
    async (supabase: SupabaseClient<Database>, userId: string, fs: FloorSettings) => {
        if (!userId) {
            throw new Error('no user id provided');
        }
        const payload = snakecaseKeys({ userId, ...fs });
        const { error } = await supabase.from('user_floor_settings').upsert(payload);

        if (error) {
            throw error;
        }
    },
    'upsertFloorSettings',
);

export const fetchFloorSettings = withLogs(async (supabase: SupabaseClient<Database>): Promise<FloorSettings> => {
    const { data, error } = await supabase.from('user_floor_settings').select('*').maybeSingle();

    if (error) {
        throw error;
    }

    if (!data) {
        return defaultFloorSettings;
    }

    return camelcaseKeys(data) as FloorSettings;
}, 'fetchFloorSettings');

export const subscribeToFloorSettings = (
    supabase: SupabaseClient<Database>,
    handler: (payload: FloorSettings) => Promise<void>,
) => {
    return supabase
        .channel('floor_settings')
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'user_floor_settings',
            },
            async (payload) => {
                try {
                    await handler(camelcaseKeys(payload.new as any));
                } catch (error) {
                    console.error('Error processing floor setting changes:', error);
                }
            },
        )
        .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
                console.error('user floor settings subscription error:', status);
            }
        });
};
